import { Box, Button, Flex, HStack, Menu, Stack, Text } from '@chakra-ui/react';
import { CaretDown, Download, FileDoc } from '@phosphor-icons/react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import Resume from '@/components/data-display/Resume';
import { exportResumeToDocx } from '@/utils';
import ResumeAnalysisResult from './ResumeAnalysisResult';

import type { CandidateWithJobContext } from '@/types/Candidate';
import { AlertsAndFollowUpQuestion } from '@/types/AnalysisResult';
import { isStatusBeforeInterviewCompleted, ResumeType } from '@/types/enums';

export interface ResumeAnalysisRef {
  scrollToFollowUpItems: () => void;
}

const ResumeAnalysis = forwardRef<
  ResumeAnalysisRef,
  {
    candidate: CandidateWithJobContext;
    onUpdateItem: (index: number, item: AlertsAndFollowUpQuestion | null) => Promise<boolean>;
  }
>(({ candidate, onUpdateItem }, ref) => {
  const followUpItemsRef = useRef<HTMLDivElement | null>(null);
  const resumeRef = useRef<HTMLDivElement | null>(null);
  const [exportingDocx, setExportingDocx] = useState(false);

  const handleExportFormattedResumeDocx = async () => {
    if (!candidate.resume_data) return;
    setExportingDocx(true);
    try {
      await exportResumeToDocx(
        candidate.resume_data,
        `${candidate.first_name} ${candidate.last_name} Resume.docx`
      );
    } finally {
      setExportingDocx(false);
    }
  };

  useImperativeHandle(ref, () => ({
    scrollToFollowUpItems: () => {
      if (followUpItemsRef.current) {
        followUpItemsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }));

  return (
    <Flex w="100%" flexDir="column" p="32px" overflowY="auto">
      <Flex w="100%" flexDirection="row" h="100%" mt="24px">
        <Stack direction="column" flex="0.55" gap="16px">
          <Flex w="100%" justifyContent="space-between" alignItems="center">
            <Box fontSize="24px" fontWeight="bold">
              Resume
            </Box>
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button
                  bg="transparent"
                  color="primary.900"
                  w="101px"
                  h="40px"
                  p="8px 16px"
                  cursor="pointer"
                  onClick={() => {}}
                  fontSize="14px"
                  fontWeight="600"
                >
                  Download
                  <CaretDown size={24} color="#4e76ff" />
                </Button>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content borderRadius="12px">
                  <Menu.Item
                    value="original"
                    _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                    fontSize="14px"
                    cursor="pointer"
                    asChild
                  >
                    <a
                      href={candidate.resume_url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <HStack gap="8px">
                        <Download size={16} />
                        <Text fontSize="14px">Original Resume</Text>
                      </HStack>
                    </a>
                  </Menu.Item>
                  {candidate.resume_type === ResumeType.Veterans && (
                    <Menu.Item
                      value="formatted-docx"
                      _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                      fontSize="14px"
                      onClick={handleExportFormattedResumeDocx}
                      disabled={exportingDocx}
                      cursor={exportingDocx ? 'not-allowed' : 'pointer'}
                    >
                      <HStack gap="8px">
                        <FileDoc size={16} />
                        <Text fontSize="14px">
                          {exportingDocx ? 'Exporting...' : 'Formatted Resume'}
                        </Text>
                      </HStack>
                    </Menu.Item>
                  )}
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          </Flex>
          <Box maxW="800px" ref={resumeRef}>
            <Resume resume={candidate.resume_data} />
          </Box>
        </Stack>
        <Box w="24px" />
        <Flex flex="0.45" flexDirection="column" overflowY="auto">
          <ResumeAnalysisResult
            analysisResult={candidate.resume_analysis_result}
            followUpQuestions={candidate.alerts_and_follow_up_questions}
            onUpdateItem={onUpdateItem}
            editable={isStatusBeforeInterviewCompleted(candidate.status)}
            followUpItemsRef={followUpItemsRef}
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

ResumeAnalysis.displayName = 'ResumeAnalysis';

export default ResumeAnalysis;
